/**
 * Edit-mode state for the case defaults section.
 *
 * Extracted from AdminSettingsPage.tsx (Sub-PR 6.3).
 */

import { useState } from 'react'
import { useOrgCaseDefaults } from '../../hooks'
import type { OrgCaseDefaults, UpdateOrgCaseDefaultsRequest } from '../../types/case-container'

function buildEditForm(orgDefaults: OrgCaseDefaults | null): UpdateOrgCaseDefaultsRequest {
  if (!orgDefaults) return {}
  return {
    timezone: orgDefaults.timezone,
    dateFormat: orgDefaults.dateFormat,
    timeFormat: orgDefaults.timeFormat,
    retentionPeriodDays: orgDefaults.retentionPeriodDays,
    evidenceNumberFormat: orgDefaults.evidenceNumberFormat,
    velociraptorExportPath: orgDefaults.velociraptorExportPath,
    evidenceImportPath: orgDefaults.evidenceImportPath,
    exportPath: orgDefaults.exportPath,
  }
}

interface UseCaseDefaultsFormResult {
  orgDefaults: OrgCaseDefaults | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  updating: boolean
  editing: boolean
  formData: UpdateOrgCaseDefaultsRequest
  setFormData: (data: UpdateOrgCaseDefaultsRequest) => void
  onEdit: () => void
  onCancel: () => void
  onSave: () => Promise<void>
}

export function useCaseDefaultsForm(): UseCaseDefaultsFormResult {
  const { defaults: orgDefaults, loading, error, refetch, update, updating } = useOrgCaseDefaults()

  const [editing, setEditing] = useState(false)
  const [formData, setFormData] = useState<UpdateOrgCaseDefaultsRequest>({})

  const onEdit = (): void => {
    setFormData(buildEditForm(orgDefaults))
    setEditing(true)
  }

  const onCancel = (): void => {
    setEditing(false)
    setFormData({})
  }

  const onSave = async (): Promise<void> => {
    const result = await update(formData)
    if (result) setEditing(false)
  }

  return {
    orgDefaults: orgDefaults ?? null,
    loading,
    error,
    refetch,
    updating,
    editing,
    formData,
    setFormData,
    onEdit,
    onCancel,
    onSave,
  }
}
